import { useEffect, useRef } from "react";
import { useAudioPlayback, useRoomContext } from "@livekit/components-react";
import {
  ConnectionState,
  createAudioAnalyser,
  RoomEvent,
  Track,
  type LocalAudioTrack,
  type Participant,
  type RemoteAudioTrack,
} from "livekit-client";
import {
  decodeReceipt,
  encodeReceipt,
  listenerState,
  lossBetween,
  micState,
  networkState,
  receiptKey,
  shouldSendReceipt,
  SILENT_LEVEL,
  VOICE_PATH_INTERVAL_MS,
  VOICE_RECEIPT_TOPIC,
  type ConnectionQualityValue,
  type ReceiptPlan,
  type ReceivedReceipt,
  type SentReceipt,
  type VoicePath,
} from "../lib/voicePath";
import { videoService } from "../api/video.api";
import {
  CallQualityTally,
  describeBrowser,
  FIRST_QUALITY_REPORT_MS,
  QUALITY_REPORT_INTERVAL_MS,
} from "../lib/callQuality";
import { useCallVolumeStore } from "../store/callVolumeStore";
import { useVoicePathStore } from "../store/voicePathStore";

const LEVEL_INTERVAL_MS = 250;

/**
 * Measures whether this participant's voice is getting through, and tells
 * each speaker whether their voice arrives here. Renders nothing.
 *
 * Mounted once per call, next to `CallAudioRenderer`, so the notes keep
 * flowing whichever view is on screen (full call, floating widget, guest
 * page). Every 3 s it reads stats locally (one read on the outgoing mic, one
 * per incoming mic -- no network). A ~40-byte note goes out only when what
 * it says changes, or every 15 s, and only to participants whose mic is on
 * (see lib/voicePath.ts).
 *
 * With `report`, it also adds up the call's quality (lib/callQuality.ts) and
 * sends it for the super admin's call report 15 s in, then once a minute,
 * and on leaving.
 */
export function VoicePathMonitor({ report }: { report?: { roomId: string; token: string } }) {
  const room = useRoomContext();
  const { canPlayAudio } = useAudioPlayback(room);
  const volume = useCallVolumeStore((s) => s.volume);
  const playingRef = useRef(false);
  playingRef.current = canPlayAudio && volume > 0;
  const canPlayRef = useRef(canPlayAudio);
  canPlayRef.current = canPlayAudio;
  // Latest room and token: the token is re-minted on a rejoin.
  const reportRef = useRef(report);
  reportRef.current = report;

  useEffect(() => {
    const { setPath, setLevel, reset } = useVoicePathStore.getState();
    const receipts = new Map<string, ReceivedReceipt>();
    const onData = (payload: Uint8Array, participant?: Participant, _kind?: unknown, topic?: string) => {
      if (topic !== VOICE_RECEIPT_TOPIC || !participant) return;
      const receipt = decodeReceipt(payload);
      if (receipt) receipts.set(participant.identity, { receipt, at: Date.now() });
    };
    room.on(RoomEvent.DataReceived, onData);

    // ── The call report: added up every sample, sent once a minute ─────────
    const tally = new CallQualityTally();
    let lastTick = Date.now();
    const onReconnecting = () => tally.reconnected();
    room.on(RoomEvent.Reconnecting, onReconnecting);
    const sendReport = (keepalive: boolean) => {
      const target = reportRef.current;
      if (!target || tally.observedMs === 0) return;
      void videoService.reportCallQuality(
        target.roomId,
        target.token,
        tally.report(room.localParticipant.sid, describeBrowser(navigator.userAgent)),
        { keepalive },
      );
    };
    const firstReportTimer = window.setTimeout(() => sendReport(false), FIRST_QUALITY_REPORT_MS);
    const reportTimer = window.setInterval(() => sendReport(false), QUALITY_REPORT_INTERVAL_MS);
    // A closed tab never unmounts; keepalive lets this last one through.
    const onPageHide = () => sendReport(true);
    window.addEventListener("pagehide", onPageHide);

    // ── Mic level, several times a second ──────────────────────────────────
    let analyser: ReturnType<typeof createAudioAnalyser> | null = null;
    let analysed: MediaStreamTrack | null = null;
    let level: number | null = null;
    let lastSound = Date.now();
    const micTrack = () =>
      room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as LocalAudioTrack | undefined;
    const sampleLevel = () => {
      const track = micTrack();
      const stream = track?.mediaStreamTrack ?? null;
      if (stream !== analysed) {
        // A new device, or a restarted track: measure the one now in use.
        void analyser?.cleanup().catch(() => {});
        analyser = null;
        analysed = stream;
        lastSound = Date.now();
        if (track) {
          try {
            analyser = createAudioAnalyser(track, { minDecibels: -90, maxDecibels: -10, smoothingTimeConstant: 0.5 });
          } catch {
            analyser = null;
          }
        }
      }
      if (!analyser || analyser.analyser.context.state !== "running" || track?.isMuted) {
        level = null;
        lastSound = Date.now();
      } else {
        level = analyser.calculateVolume();
        if (level > SILENT_LEVEL) lastSound = Date.now();
      }
      setLevel(level);
    };
    const levelTimer = window.setInterval(sampleLevel, LEVEL_INTERVAL_MS);

    // ── Everything else, every few seconds ─────────────────────────────────
    let prevSender: { sent: number; lost: number } | null = null;
    const prevReceived = new Map<string, number>();
    let lastSent: SentReceipt | null = null;
    let unmutedSince: number | null = null;
    let busy = false;

    const tick = async () => {
      if (busy) return;
      busy = true;
      try {
        const now = Date.now();
        const local = room.localParticipant;
        const micPub = local.getTrackPublication(Track.Source.Microphone);
        const track = micTrack();
        const sending = !!track && !micPub?.isMuted;
        if (!sending) unmutedSince = null;
        else if (unmutedSince === null) unmutedSince = now;

        // 2. Network: what the server reports about our outgoing audio.
        let packetsSentDelta: number | null = null;
        let lossPct: number | null = null;
        let rttMs: number | null = null;
        if (track && sending) {
          const stats = await track.getSenderStats().catch(() => undefined);
          if (stats?.packetsSent !== undefined) {
            const next = { sent: stats.packetsSent, lost: stats.packetsLost ?? 0 };
            packetsSentDelta = prevSender ? next.sent - prevSender.sent : null;
            lossPct = lossBetween(prevSender, next);
            prevSender = next;
          }
          if (stats?.roundTripTime !== undefined) rttMs = Math.round(stats.roundTripTime * 1000);
        } else {
          prevSender = null;
        }

        // 3. Note out: which speakers' audio is not arriving here. Sent
        // only to speakers, and only when it changes or a heartbeat is due.
        const plan: ReceiptPlan = { missing: [], arriving: [], playing: playingRef.current, destinations: [] };
        for (const participant of room.remoteParticipants.values()) {
          const pub = participant.getTrackPublication(Track.Source.Microphone);
          if (!pub || pub.isMuted) continue;
          plan.destinations.push(participant.identity);
          const remote = pub.track as RemoteAudioTrack | undefined;
          if (!remote) {
            // Unmuted, yet not subscribed: nothing can be arriving.
            plan.missing.push(participant.identity);
            continue;
          }
          const stats = await remote.getReceiverStats().catch(() => undefined);
          // Bytes, not packets: LiveKit's audio receiver stats leave
          // packetsReceived out (checked against livekit-client 2.19).
          const received = stats?.bytesReceived ?? stats?.packetsReceived;
          if (received === undefined) continue;
          const before = prevReceived.get(pub.trackSid);
          prevReceived.set(pub.trackSid, received);
          if (before === undefined) continue; // first sample: no verdict yet
          (received > before ? plan.arriving : plan.missing).push(participant.identity);
        }
        if (room.state === ConnectionState.Connected && shouldSendReceipt(plan, lastSent, now)) {
          lastSent = { key: receiptKey(plan), at: now };
          void local
            .publishData(encodeReceipt({ v: 2, missing: plan.missing, playing: plan.playing }), {
              // Rare now, so reliable: a lost change would otherwise wait
              // for the next heartbeat.
              reliable: true,
              topic: VOICE_RECEIPT_TOPIC,
              destinationIdentities: plan.destinations,
            })
            .catch(() => {
              lastSent = null;
            });
        }

        const path: VoicePath = {
          mic: micState({
            published: !!track,
            muted: !!micPub?.isMuted,
            level,
            quietForMs: now - lastSound,
          }),
          micLevel: level,
          network: networkState({
            connected: room.state === ConnectionState.Connected,
            micSending: sending,
            packetsSentDelta,
            lossPct,
            rttMs,
            quality: local.connectionQuality as ConnectionQualityValue,
          }),
          lossPct,
          rttMs,
          quality: local.connectionQuality as ConnectionQualityValue,
          listeners: Array.from(room.remoteParticipants.values())
            // Agents (the meeting twin) listen but send no receipts.
            .filter((p) => !p.isAgent)
            .map((p) => ({
              identity: p.identity,
              name: p.name || p.identity,
              state: listenerState(local.identity, receipts.get(p.identity), { now, unmutedSince }),
            })),
        };
        setPath(path);
        tally.add({
          elapsedMs: now - lastTick,
          path,
          notArriving: plan.missing,
          connected: room.state === ConnectionState.Connected,
          canPlayAudio: canPlayRef.current,
        });
        lastTick = now;
      } finally {
        busy = false;
      }
    };
    void tick();
    const tickTimer = window.setInterval(() => void tick(), VOICE_PATH_INTERVAL_MS);

    return () => {
      sendReport(true);
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.Reconnecting, onReconnecting);
      window.removeEventListener("pagehide", onPageHide);
      window.clearTimeout(firstReportTimer);
      window.clearInterval(reportTimer);
      window.clearInterval(levelTimer);
      window.clearInterval(tickTimer);
      void analyser?.cleanup().catch(() => {});
      reset();
    };
  }, [room]);

  return null;
}
